/**
 * Toggle Collection Swiper Functionality
 * Handles SwiperJS initialization for toggle collection list sections
 */

// Type declarations for global objects
/* global Swiper */

// Guard: avoid redeclaration in Theme Editor when assets are re-injected
if (!window.SuniceToggleCollectionSwiper) {
  class SuniceToggleCollectionSwiper {
    static getInstance() {
      if (!SuniceToggleCollectionSwiper._instance) {
        SuniceToggleCollectionSwiper._instance = new SuniceToggleCollectionSwiper();
      }
      return SuniceToggleCollectionSwiper._instance;
    }
    constructor() {
      this.swiperInstances = new Map();
    }
  
    initializeToggleCollectionSwiper(swiperContainer) { // @ts-ignore
      if (!swiperContainer) return;
      
      const containerId = swiperContainer.id || `toggle-collection-swiper-${Date.now()}`;
      if (!swiperContainer.id) {
        swiperContainer.id = containerId;
      }
      
      // Check if this swiper is already initialized - destroy first if so (for theme editor)
      if (this.swiperInstances.has(containerId)) {
        const existingSwiper = this.swiperInstances.get(containerId);
        if (existingSwiper && existingSwiper.destroy) {
          existingSwiper.destroy(true, true);
        }
        this.swiperInstances.delete(containerId);
      }
      
      // Reset initialization flags
      swiperContainer.classList.remove('swiper-initialized');
      
      // Get settings from the swiper container's data attributes
      const showArrows = swiperContainer.dataset.showArrows !== 'false';
      const autoplay = swiperContainer.dataset.autoplay === 'true';
      const autoplaySpeed = swiperContainer.dataset.autoplaySpeed || 5000;
      const infinite = swiperContainer.dataset.infinite !== 'false';
      
      // Get column count from dataset or fallback to CSS variables
      const columnCount = parseInt(swiperContainer.dataset.columnCount) || 
                         parseInt(swiperContainer.closest('[style*="--toggle-collection-column-count"]')?.style.getPropertyValue('--toggle-collection-column-count')) || 
                         4;
      const mobileColumnCount = parseInt(swiperContainer.dataset.mobileColumnCount) || 
                               parseInt(swiperContainer.closest('[style*="--toggle-collection-mobile-column-count"]')?.style.getPropertyValue('--toggle-collection-mobile-column-count')) || 
                               1;
      
      // Find the navigation buttons in the parent wrapper (DOM order: previous then next)
      const wrapper = swiperContainer.closest('.swiper-container-wrapper');
      const prevButton = wrapper ? wrapper.querySelector('.slideshow-control--previous') : null;
      const nextButton = wrapper ? wrapper.querySelector('.slideshow-control--next') : null;

      const slideCount = swiperContainer.querySelectorAll('.swiper-slide').length;
      const canLoop = infinite && slideCount >= 2;

      // Force buttons to be visible
      if (nextButton) {
        nextButton.style.display = 'flex';
        nextButton.style.visibility = 'visible';
        nextButton.style.opacity = '1';
        nextButton.classList.remove('button-lock');
      }
      
      if (prevButton) {
        prevButton.style.display = 'flex';
        prevButton.style.visibility = 'visible';
        prevButton.style.opacity = '1';
        prevButton.classList.remove('button-lock');
      }
      
      // Toggle Collection specific swiper configuration with enhanced infinite sliding
      // OPTIMIZED: Use correct initial column count immediately
      const viewportWidth = window.innerWidth;
      let initialColumnCount = 1.5;
      if (viewportWidth >= 1513) {
        initialColumnCount = columnCount;
      } else if (viewportWidth >= 1200) {
        initialColumnCount = 4;
      } else if (viewportWidth >= 768) {
        initialColumnCount = 3;
      }
      
      const swiperConfig = {
        slidesPerView: 1.5,
        spaceBetween: 16,
        centeredSlides: true,
        loop: canLoop,
        loopedSlides: slideCount,
        speed: 400,
        grabCursor: true,
        navigation: nextButton && prevButton ? {
          nextEl: nextButton,
          prevEl: prevButton,
        } : false,
        breakpoints: {
          768: {
            slidesPerView: 3,
            spaceBetween: 16,
            centeredSlides: false,
          },
          1200: {
            slidesPerView: 4,
            spaceBetween: 16,
            centeredSlides: false,
          },
          1513: {
            slidesPerView: 5,
            spaceBetween: 16,
            centeredSlides: false,
          }
        },
      };
  
      let swiper;
      try {
        swiper = new window.Swiper(swiperContainer, swiperConfig);
      } catch (e) {
        swiperConfig.loop = false;
        swiper = new window.Swiper(swiperContainer, swiperConfig);
      }
      this.swiperInstances.set(containerId, swiper);

      // Mark as initialized immediately so lazy-loaded sections can reveal on next frame
      swiperContainer.classList.add('swiper-initialized');
      requestAnimationFrame(() => {
        swiperContainer.style.minHeight = swiperContainer.offsetHeight + 'px'; // Prevent CLS
        void swiperContainer.offsetWidth; // Force reflow for transition
      });

      return swiper;
    }
  
    // Method to enable infinite sliding on existing swipers
    enableInfiniteSliding(containerId) { // @ts-ignore
      const swiper = this.swiperInstances.get(containerId);
      if (swiper && swiper.loop) {
        swiper.loopCreate();
        
      }
    }
  
    // Method to disable infinite sliding on existing swipers
    disableInfiniteSliding(containerId) { // @ts-ignore
      const swiper = this.swiperInstances.get(containerId);
      if (swiper && swiper.loop) {
        swiper.loopDestroy();
        
      }
    }
  
    // Method to manually trigger infinite slide
    triggerInfiniteSlide(containerId, direction = 'next') { // @ts-ignore
      const swiper = this.swiperInstances.get(containerId);
      if (swiper) {
        if (direction === 'next') {
          swiper.slideNext();
        } else {
          swiper.slidePrev();
        }
      }
    }
  
    destroyToggleCollectionSwipers() {
      this.swiperInstances.forEach((swiper, id) => {
        if (swiper && swiper.destroy) {
          swiper.destroy(true, true);
        }
      });
      this.swiperInstances.clear();
    }
  }
  
  // Export for use in main swiper file
  // @ts-ignore
  window.SuniceToggleCollectionSwiper = SuniceToggleCollectionSwiper;
}
  